import React from "react";
import { CButton, CFormTextarea } from "@coreui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { Formik } from "formik";
import { referralRecord } from "@/constants/translation/referralRecord.translation";

/**
 * Shared reason-required "Mark as Fraudulent" confirm dialog for Referral Records.
 *
 * Cloned from `handleRejectWithReason` in
 * src/pages/user/freelancer/[freelancer_user_id]/view/index.js — confirmAlert + Formik with a
 * required CFormTextarea reason field. Extracted here so both the Referral Records list page
 * (flag icon row action) and the Referral Record view page (header action) share one
 * implementation instead of duplicating the dialog markup.
 *
 * @param {Object} params
 * @param {function} params.t - Translation function from useTranslationContext.
 * @param {string} params.referralUuid - Referral UUID to flag as fraudulent.
 * @param {function} params.onConfirm - Called with (referralUuid, reason) once the reason is submitted.
 */
export const showMarkFraudulentDialog = ({ t, referralUuid, onConfirm }) => {
  const popup = referralRecord.view.mark_fraudulent_popup;

  confirmAlert({
    customUI: ({ onClose }) => (
      <Formik
        initialValues={{ reason: "" }}
        validate={(values) => {
          const errors = {};
          if (!values.reason || !values.reason.trim()) {
            errors.reason = t(popup.reason_required);
          }
          return errors;
        }}
        onSubmit={(values, { setSubmitting }) => {
          onConfirm(referralUuid, values.reason.trim());
          setSubmitting(false);
          onClose();
        }}
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
          <div className="alert-box admin-status-popup">
            <div className="alert-popup">
              <FontAwesomeIcon className="alert-close" icon={faClose} onClick={onClose} />
              <h2 className="alert-title">{t(popup.heading)}</h2>
              <p className="text-center mb-3">{t(popup.message)}</p>
              <div className="mb-3 text-start">
                <CFormTextarea
                  id="reason"
                  name="reason"
                  rows={3}
                  placeholder={t(popup.reason_placeholder)}
                  value={values.reason}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  invalid={touched.reason && !!errors.reason}
                />
                {touched.reason && errors.reason && <div className="text-danger small mt-1">{errors.reason}</div>}
              </div>
              <div className="d-flex gap-2 justify-content-center flex-nowrap">
                <CButton color="primary" className="table-delete-button" disabled={isSubmitting} onClick={handleSubmit}>
                  {t(popup.yes_btn_text)}
                </CButton>
                <CButton className="table-primary-button" color="danger" onClick={onClose} disabled={isSubmitting}>
                  {t(popup.no_btn_text)}
                </CButton>
              </div>
            </div>
          </div>
        )}
      </Formik>
    ),
  });
};

export default showMarkFraudulentDialog;
